import { NetworkUser, Connection, ConnectionType, ConnectionFilter } from '../types/network';

// Mock current user
export const CURRENT_USER_ID = 'user-1';

// Mock users for network
export const mockNetworkUsers: NetworkUser[] = [
  { id: 'user-1', name: 'John Doe', profession: 'Software Engineer', city: 'San Francisco' },
  { id: 'user-2', name: 'Jane Smith', profession: 'Product Designer', city: 'New York' },
  { id: 'user-3', name: 'Bob Johnson', profession: 'Marketing Manager', city: 'Chicago' },
  { id: 'user-4', name: 'Alice Williams', profession: 'Data Scientist', city: 'Seattle' },
  { id: 'user-5', name: 'Charlie Brown', profession: 'UX Designer', city: 'Austin' },
  { id: 'user-6', name: 'Diana Prince', profession: 'Business Analyst', city: 'Boston' },
  { id: 'user-7', name: 'Ethan Hunt', profession: 'DevOps Engineer', city: 'Denver' },
  { id: 'user-8', name: 'Fiona Green', profession: 'Content Writer', city: 'Portland' },
  { id: 'user-9', name: 'George Miller', profession: 'Project Manager', city: 'Miami' },
  { id: 'user-10', name: 'Hannah Lee', profession: 'Frontend Developer', city: 'Los Angeles' },
];

// Mock connections
export const mockConnections: Connection[] = [
  // Accepted connections with types
  { id: 'conn-1', userId: 'user-1', connectedUserId: 'user-2', status: 'accepted', connectionType: 'mentor', createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) },
  { id: 'conn-2', userId: 'user-1', connectedUserId: 'user-3', status: 'accepted', connectionType: 'client', createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000) },
  { id: 'conn-3', userId: 'user-1', connectedUserId: 'user-4', status: 'accepted', connectionType: 'supplier', createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) },
  
  // Incoming requests (someone sent request to current user)
  { id: 'conn-4', userId: 'user-5', connectedUserId: 'user-1', status: 'pending', createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000) },
  { id: 'conn-5', userId: 'user-6', connectedUserId: 'user-1', status: 'pending', createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000) },
  
  // Outgoing requests (current user sent request to someone)
  { id: 'conn-6', userId: 'user-1', connectedUserId: 'user-7', status: 'pending', createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000) },
  { id: 'conn-7', userId: 'user-1', connectedUserId: 'user-8', status: 'pending', createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000) },
];

// Helper functions
export const getUserById = (id: string): NetworkUser | undefined => {
  return mockNetworkUsers.find(user => user.id === id);
};

export const getAcceptedConnections = (userId: string, filter: ConnectionFilter = 'all'): Array<{ user: NetworkUser; connectionType?: ConnectionType }> => {
  const connections = mockConnections.filter(
    conn => conn.status === 'accepted' && (conn.userId === userId || conn.connectedUserId === userId)
  );
  
  let filteredConnections = connections;
  if (filter !== 'all') {
    filteredConnections = connections.filter(conn => conn.connectionType === filter);
  }
  
  return filteredConnections.map(conn => {
    const connectedUserId = conn.userId === userId ? conn.connectedUserId : conn.userId;
    const user = getUserById(connectedUserId);
    return user ? { user, connectionType: conn.connectionType } : null;
  }).filter((item): item is { user: NetworkUser; connectionType?: ConnectionType } => item !== null);
};

export const getIncomingRequests = (userId: string): NetworkUser[] => {
  const requests = mockConnections.filter(
    conn => conn.status === 'pending' && conn.connectedUserId === userId
  );
  
  return requests.map(req => getUserById(req.userId)).filter((user): user is NetworkUser => user !== undefined);
};

export const getOutgoingRequests = (userId: string): NetworkUser[] => {
  const requests = mockConnections.filter(
    conn => conn.status === 'pending' && conn.userId === userId
  );
  
  return requests.map(req => getUserById(req.connectedUserId)).filter((user): user is NetworkUser => user !== undefined);
};

export const getConnectionStatus = (currentUserId: string, targetUserId: string): 'connected' | 'pending' | 'none' => {
  const connection = mockConnections.find(
    conn => 
      (conn.userId === currentUserId && conn.connectedUserId === targetUserId) ||
      (conn.userId === targetUserId && conn.connectedUserId === currentUserId)
  );
  
  if (!connection) return 'none';
  if (connection.status === 'accepted') return 'connected';
  return 'pending';
};

export const searchUsers = (query: string, currentUserId: string): NetworkUser[] => {
  if (!query.trim()) return [];
  
  const lowerQuery = query.toLowerCase();
  return mockNetworkUsers.filter(user => {
    if (user.id === currentUserId) return false; // Exclude current user
    
    return (
      user.name.toLowerCase().includes(lowerQuery) ||
      user.profession.toLowerCase().includes(lowerQuery)
    );
  });
};

// Mock actions (in real app, these would call backend APIs)
export const sendConnectionRequest = (fromUserId: string, toUserId: string, connectionType: ConnectionType): void => {
  const newConnection: Connection = {
    id: `conn-${Date.now()}`,
    userId: fromUserId,
    connectedUserId: toUserId,
    status: 'pending',
    connectionType,
    createdAt: new Date(),
  };
  mockConnections.push(newConnection);
};

export const acceptConnectionRequest = (fromUserId: string, toUserId: string, connectionType: ConnectionType): void => {
  const connection = mockConnections.find(
    conn => conn.userId === fromUserId && conn.connectedUserId === toUserId && conn.status === 'pending'
  );
  if (connection) {
    connection.status = 'accepted';
    connection.connectionType = connectionType;
  }
};

export const rejectConnectionRequest = (fromUserId: string, toUserId: string): void => {
  const index = mockConnections.findIndex(
    conn => conn.userId === fromUserId && conn.connectedUserId === toUserId && conn.status === 'pending'
  );
  if (index !== -1) {
    mockConnections.splice(index, 1);
  }
};

export const removeConnection = (userId: string, connectedUserId: string): void => {
  const index = mockConnections.findIndex(
    conn => 
      conn.status === 'accepted' &&
      ((conn.userId === userId && conn.connectedUserId === connectedUserId) ||
       (conn.userId === connectedUserId && conn.connectedUserId === userId))
  );
  if (index !== -1) {
    mockConnections.splice(index, 1);
  }
};
